/**
 * Компонент настройки бокового меню.
 * Позволяет перетаскивать пункты и скрывать ненужные разделы.
 */
import React, { useState, useEffect } from 'react';
import { Drawer, Switch, Button, Space, Typography, Tooltip, message } from 'antd';
import { UndoOutlined, HolderOutlined, EditOutlined } from '@ant-design/icons';
import { useMenuConfigStore, type MenuItemConfig } from '../store/menuStore';

interface SourceMenuItem {
  key: string;
  icon?: React.ReactNode;
  label: string | React.ReactNode;
  children?: Array<{ key: string; label: string | React.ReactNode }>;
}

interface MenuConfiguratorProps {
  allMenuItems: SourceMenuItem[];
  tenantId?: number;
  collapsed?: boolean;
}

// ── Перетаскиваемый элемент ──
const DraggableItem: React.FC<{
  item: MenuItemConfig;
  index: number;
  icon?: React.ReactNode;
  label: string | React.ReactNode;
  dragType: string;
  parentKey?: string;
  onToggle: () => void;
  onDragStart: (e: React.DragEvent, index: number) => void;
  onDrop: (e: React.DragEvent, toIndex: number) => void;
}> = ({ item, index, icon, label, dragType, parentKey, onToggle, onDrop }) => {
  const [isDragOver, setIsDragOver] = useState(false);

  return (
    <div
      draggable
      onDragStart={(e) => {
        e.stopPropagation();
        e.dataTransfer.setData('application/json', JSON.stringify({ type: dragType, parentKey, index }));
        e.dataTransfer.effectAllowed = 'move';
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
        // Разрешаем drop только для того же типа
        try {
          e.dataTransfer.dropEffect = 'move';
        } catch {}
        setIsDragOver(true);
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
        try {
          const data = JSON.parse(e.dataTransfer.getData('application/json'));
          if (data.type === dragType && data.parentKey === parentKey && data.index !== index) {
            onDrop(e, index);
          }
        } catch {}
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '10px 12px',
        borderRadius: 8,
        background: isDragOver ? '#fff7e6' : item.visible ? '#fff' : '#fafafa',
        border: `1px solid ${isDragOver ? '#ffc069' : '#f0f0f0'}`,
        marginBottom: 6,
        cursor: 'grab',
        opacity: item.visible ? 1 : 0.5,
        transition: 'all 0.2s',
        userSelect: 'none',
      }}
    >
      <HolderOutlined style={{ color: '#bfbfbf', marginRight: 10, fontSize: 14 }} />
      {icon && <span style={{ marginRight: 8, fontSize: 16, display: 'flex', alignItems: 'center' }}>{icon}</span>}
      <span style={{ flex: 1, fontSize: 14 }}>{label}</span>
      <Switch
        size="small"
        checked={item.visible}
        onChange={(_checked, e) => {
          e.stopPropagation();
          onToggle();
        }}
      />
    </div>
  );
};

const MenuConfigurator: React.FC<MenuConfiguratorProps> = ({ allMenuItems, tenantId, collapsed }) => {
  const [open, setOpen] = useState(false);
  const { configs, load, save, setConfigs, toggleVisibility, moveItem, moveChild, reset } = useMenuConfigStore();

  useEffect(() => {
    load(tenantId);
  }, [tenantId]);

  // Конфиги: из стора или дефолтные из allMenuItems
  const effectiveConfigs = configs.length > 0
    ? configs
    : allMenuItems.map(item => ({
        key: item.key,
        visible: true,
        children: item.children?.map(ch => ({ key: ch.key, visible: true })),
      }));

  const handleOpen = () => {
    if (configs.length === 0) {
      setConfigs(allMenuItems.map(item => ({
        key: item.key,
        visible: true,
        children: item.children?.map(ch => ({ key: ch.key, visible: true })),
      })));
    }
    setOpen(true);
  };

  const handleSave = () => {
    save(tenantId);
    message.success('Настройки меню сохранены');
    setOpen(false);
  };

  const handleReset = () => {
    reset(tenantId);
    message.info('Рекомендованный порядок разделов восстановлен');
    setOpen(false);
  };

  // Поиск информации о пункте по ключу
  const getInfo = (key: string): SourceMenuItem | undefined => {
    for (const item of allMenuItems) {
      if (item.key === key) return item;
      if (item.children) {
        for (const ch of item.children) {
          if (ch.key === key) return ch as SourceMenuItem;
        }
      }
    }
    return undefined;
  };

  const handleParentDrop = (_e: React.DragEvent, toIndex: number) => {
    try {
      const data = JSON.parse(_e.dataTransfer.getData('application/json'));
      moveItem(data.index, toIndex);
    } catch {}
  };

  const handleChildDrop = (parentKey: string) => (_e: React.DragEvent, toIndex: number) => {
    try {
      const data = JSON.parse(_e.dataTransfer.getData('application/json'));
      moveChild(parentKey, data.index, toIndex);
    } catch {}
  };

  return (
    <>
      <Tooltip title={collapsed ? 'Настроить меню' : ''} placement="right">
        <div
          onClick={handleOpen}
          style={{
            padding: collapsed ? '12px 0' : '12px 24px',
            cursor: 'pointer',
            color: 'rgba(255,255,255,0.65)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            gap: 10,
            borderTop: '1px solid rgba(255,255,255,0.06)',
            transition: 'all 0.2s',
            fontSize: 14,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = '#fff'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.65)'; e.currentTarget.style.background = 'transparent'; }}
        >
          <EditOutlined style={{ fontSize: 16 }} />
          {!collapsed && <span>Настроить меню</span>}
        </div>
      </Tooltip>

      <Drawer
        title="Настройка меню"
        placement="left"
        open={open}
        onClose={() => setOpen(false)}
        width={380}
        styles={{ body: { padding: '16px' } }}
        footer={
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Button onClick={handleReset} icon={<UndoOutlined />}>Вернуть порядок</Button>
            <Button type="primary" onClick={handleSave}>Сохранить</Button>
          </Space>
        }
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 16, fontSize: 13 }}>
          Перетаскивайте пункты для изменения порядка.
          Используйте переключатель для скрытия разделов. Если у компании сохранён старый порядок,
          нажмите «Вернуть порядок», чтобы применить новую структуру панели.
        </Typography.Paragraph>

        {effectiveConfigs.map((config, index) => {
          const info = getInfo(config.key);
          if (!info) return null;

          return (
            <div key={config.key}>
              <DraggableItem
                item={config}
                index={index}
                icon={'icon' in info ? info.icon : undefined}
                label={info.label}
                dragType="parent"
                onToggle={() => toggleVisibility(config.key)}
                onDragStart={() => {}}
                onDrop={handleParentDrop}
              />

              {/* Подменю — показываем если родитель видим и есть дети */}
              {config.children && config.visible && (
                <div style={{ marginLeft: 28, marginBottom: 8 }}>
                  {config.children.map((childConfig, childIndex) => {
                    const childInfo = getInfo(childConfig.key);
                    if (!childInfo) return null;
                    return (
                      <DraggableItem
                        key={childConfig.key}
                        item={childConfig}
                        index={childIndex}
                        label={childInfo.label}
                        dragType="child"
                        parentKey={config.key}
                        onToggle={() => toggleVisibility(childConfig.key, config.key)}
                        onDragStart={() => {}}
                        onDrop={handleChildDrop(config.key)}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </Drawer>
    </>
  );
};

export default MenuConfigurator;
